/* ----------------------------------------------------------------------------
 * voiceDiag — GATED frontend diagnostics beacon (owner Android voice retest).
 *
 * WHY: the backend-only diagnostics prove STT + assist routing SUCCEED on
 * Android (transcript captured @0.998, route=briefing). The owner-reported
 * failure ("error / listening loop") is DOWNSTREAM in the FE response/playback,
 * which the backend cannot see. This beacon POSTs the missing FE events —
 * send / assist_result / state_transition / tts_segment / audioctx_state /
 * error / recorder_restart — to the SAME evidence.log the backend diag writes,
 * so the next real-device retest reveals the exact failure point.
 *
 * STRICT NO-OP unless enabled. Enable signal: GET /v1/ai/voice/status returns
 * `diag: true` (derived server-side from AURA_VOICE_DIAG_ENABLED). The flag is
 * read ONCE per session; until it resolves, events are held in a small bounded
 * buffer and either flushed (enabled) or dropped (disabled). Every send is
 * fire-and-forget: it NEVER throws and NEVER blocks a voice turn.
 *
 * Correlation: one client-generated `cid` per session ties the FE events
 * together (and places them alongside the backend lines in evidence.log).
 *
 * TEMPORARY: remove together with the backend flag + sink once the Android
 * root cause is confirmed.
 * -------------------------------------------------------------------------- */

import { getVoiceStatus, postVoiceDiag } from './voiceApi.ts';
import type { SttResultMeta } from '../stt/SttAdapter.ts';

/** undefined = not yet probed; true/false = resolved enable state. */
let enabled: boolean | undefined;
let probing = false;
let sessionCid: string | null = null;

interface PendingEvent {
  event: string;
  fields: Record<string, unknown>;
  /** per-CAPTURE cid override (stt_result) so the FE capture line joins the
   *  backend ffprobe + Deepgram rows on the SAME capture cid; other lifecycle
   *  events keep the session cid. */
  cidOverride?: string;
}
const BUFFER_CAP = 50;
const buffer: PendingEvent[] = [];

/** One stable per-session correlation id (prefixed so FE lines are obvious). */
function cid(): string {
  if (sessionCid) return sessionCid;
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      sessionCid = 'fe_' + crypto.randomUUID();
    }
  } catch {
    /* no-op */
  }
  if (!sessionCid) sessionCid = 'fe_' + Math.random().toString(16).slice(2) + Date.now().toString(16);
  return sessionCid;
}

/** The stable per-FE-session correlation id (fe_...), SHARED with the voice
 *  lease so a client's diag beacons and its lease holder token are the same
 *  cid — one browser session = one token across arbitration + diagnostics. */
export function voiceSessionCid(): string {
  return cid();
}

/** Read the enable flag exactly once (fail-closed to OFF on any error). */
async function ensureProbe(): Promise<void> {
  if (enabled !== undefined || probing) return;
  probing = true;
  try {
    const status = await getVoiceStatus();
    enabled = status?.diag === true;
  } catch {
    enabled = false; // fail-closed: no diagnostics when the probe fails
  } finally {
    probing = false;
    if (enabled) flush();
    else buffer.length = 0; // strict no-op when disabled: drop anything buffered
  }
}

/** Eagerly resolve the enable flag (call once on controller mount). */
export function initVoiceDiag(): void {
  void ensureProbe();
}

/** Synchronous read of the ALREADY-RESOLVED diag flag (voice residual #2, raw-
 *  audio instrumentation): true only once the one-time probe above has resolved
 *  ON. Undefined/false (not yet resolved, or resolved OFF) both read as false --
 *  fail-closed, exactly like the beacon above (never blocks/awaits the probe
 *  itself; a caller on the hot wake path must never await network). */
export function isVoiceDiagEnabled(): boolean {
  return enabled === true;
}

function flush(): void {
  const pending = buffer.splice(0, buffer.length);
  for (const e of pending) void send(e.event, e.fields, e.cidOverride);
}

/** Cap string values so the POST body stays small and PII-bounded. */
function sanitize(fields: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'string') out[k] = v.length > 300 ? v.slice(0, 300) : v;
    else if (typeof v === 'number' || typeof v === 'boolean') out[k] = v;
    else out[k] = String(v).slice(0, 300);
  }
  return out;
}

async function send(event: string, fields: Record<string, unknown>, cidOverride?: string): Promise<void> {
  try {
    // cidOverride (stt_result's per-capture cid) wins over the session cid so the
    // FE capture line JOINS the backend ffprobe + Deepgram rows on ONE cid.
    await postVoiceDiag({ cid: cidOverride && cidOverride !== '' ? cidOverride : cid(), event, ...sanitize(fields) });
  } catch {
    /* diagnostics must never disturb the turn */
  }
}

/** Core beacon. Strict no-op when disabled; buffers until the flag resolves. */
export function voiceBeacon(event: string, fields: Record<string, unknown> = {}, cidOverride?: string): void {
  if (enabled === false) return; // resolved OFF: strict no-op
  if (enabled === undefined) {
    if (buffer.length < BUFFER_CAP) buffer.push({ event, fields, cidOverride });
    void ensureProbe();
    return;
  }
  void send(event, fields, cidOverride);
}

// --- typed helpers (one per event the owner asked to inspect) ---------------

export function diagSend(utterance: string): void {
  voiceBeacon('send', { utterance, len: utterance.length });
}
export function diagAssistResult(ok: boolean, httpStatus: number, ms: number): void {
  voiceBeacon('assist_result', { ok, http_status: httpStatus, ms });
}
export function diagStateTransition(from: string, to: string, trigger?: string): void {
  // NOTE: the trigger is stored as `trigger` (NOT `event`) so it never collides
  // with / overwrites the beacon envelope's own `event` name in the POST body.
  voiceBeacon('state_transition', { from, to, trigger: trigger ?? '-' });
}
export function diagTtsSegment(index: number, synth: 'ok' | 'fail', playOutcome?: string): void {
  voiceBeacon('tts_segment', { index, synth, play_outcome: playOutcome ?? '-' });
}
export function diagAudioCtx(unlocked: boolean, needsUserPlay: boolean): void {
  voiceBeacon('audioctx_state', { unlocked, needs_user_play: needsUserPlay });
}
export function diagError(code: string, message: string, stack?: string): void {
  const firstLine = typeof stack === 'string' ? stack.split('\n')[0] : '';
  voiceBeacon('error', { code, message: message ?? '', stack_first_line: firstLine ?? '-' });
}
export function diagRecorderRestart(reason: string): void {
  voiceBeacon('recorder_restart', { reason });
}
/** STT capture/transcription result (owner Android retest). PII-safe: emits the
 *  action the adapter took + capture sizes/timing + confidence + transcript
 *  LENGTH — never the transcript TEXT (already captured server-side as
 *  stt.result). Closes the FE-side evidence gap: a low-confidence transcript the
 *  adapter DROPS (re-prompt) previously produced no FE line, so a rejected
 *  attempt was invisible on the owner's retest. */
export function diagSttResult(m: SttResultMeta): void {
  voiceBeacon('stt_result', {
    action: m.action,
    bytes: m.bytes,
    mime: m.mime,
    captured_ms: m.capturedMs,
    confidence: m.confidence,
    transcript_len: m.transcriptLen,
    // --- CAPTURE PARAMETERS: THREE DISTINCT TIERS, labelled so requested,
    //     getSettings-actual and recorder-declared are NEVER conflated:
    //       (a) REQUESTED         -> req_*  (MediaStreamTrack.getConstraints — what
    //                                        getUserMedia was ASKED for),
    //       (b) getSettings-ACTUAL -> sample_rate/channel_count/echo_cancellation/...
    //                                        (what the UA actually chose), and
    //       (c) recorder-DECLARED  -> mime_full/audio_bits_per_second
    //                                        (MediaRecorder's declared container/bitrate).
    //     undefined values are dropped by sanitize(), so a tier the browser did NOT
    //     expose is simply ABSENT — never zero-filled, faked, or reported as another
    //     tier's value. STILL TEXT-FREE: sizes/flags/rates + transcript LENGTH only.
    //
    // (a) REQUESTED tier — getConstraints():
    req_sample_rate: m.reqSampleRate,
    req_channel_count: m.reqChannelCount,
    req_echo_cancellation: m.reqEchoCancellation,
    req_auto_gain_control: m.reqAutoGainControl,
    req_noise_suppression: m.reqNoiseSuppression,
    req_latency: m.reqLatency,
    // (b) getSettings-ACTUAL tier:
    sample_rate: m.sampleRate,
    channel_count: m.channelCount,
    echo_cancellation: m.echoCancellation,
    auto_gain_control: m.autoGainControl,
    noise_suppression: m.noiseSuppression,
    track_latency: m.latency,
    // (c) recorder-DECLARED tier:
    mime_full: m.mimeFull,
    audio_bits_per_second: m.audioBitsPerSecond,
    // labels/context (NOT a capture tier):
    surface: m.surface,
    start_latency_ms: m.startLatencyMs,
    // The per-capture cid rides the beacon ENVELOPE (see the m.cid override arg
    // below) so the FE capture line joins the backend ffprobe + stt.result rows.
  }, m.cid);
}

/** Test-only: reset module state so each test starts clean. */
export function __resetVoiceDiagForTest(): void {
  enabled = undefined;
  probing = false;
  sessionCid = null;
  buffer.length = 0;
}
